import { useMemo } from 'react'
import { createCrmApi } from 'services/crm/api'
import { useHubSpotConnect } from 'services/crm/use-hubspot-connect'
// The protection settings the link flow and the detail drawer both render.
// The modal's steps and the drawer's Setup cards must stay the SAME fields,
// so the field groups live here once: the modal composes them per step, the
// drawer stacks them as cards. Copy changes land in both places by design.
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import {
	AppButton,
	Checkbox,
	CodeBlock,
	Collapse,
	Divider,
	DotMatrix,
	Dropdown,
	IconButton,
	Input,
	Modal,
	ModalHeader,
	Radio,
	RadioGroup,
	SegmentedTab,
	SegmentedTabList,
	SegmentedTabs,
	Toggle,
	Tooltip,
	VerificationShield,
	useEnterExit
} from '@clickguard/design-system'
import AnnotationPlus from '@untitled-ui/icons-react/build/esm/AnnotationPlus'
import AlertCircle from '@untitled-ui/icons-react/build/esm/AlertCircle'
import CheckCircle from '@untitled-ui/icons-react/build/esm/CheckCircle'
import HelpCircle from '@untitled-ui/icons-react/build/esm/HelpCircle'
import Plus from '@untitled-ui/icons-react/build/esm/Plus'
import XClose from '@untitled-ui/icons-react/build/esm/XClose'
import {
	useFormSpam,
	AuthorizeCopy,
	BOT_LEVELS,
	BotConsent,
	BotLevel,
	CRM_FIELDS,
	CaptchaTrigger,
	CrmAccount,
	CrmConnections,
	CrmProvider,
	EMAIL_RULES
} from '../form-spam-data'
import { StepList } from './step-list'
import { FORM_SPAM_KB_URL } from './form-spam-refunds-agent-tokens'

/**
 * R153 (audit 2026-08-28): `blocked` is the fourth state. `window.open`
 * returns null under a pop-up blocker, and the row used to sit on idle with
 * no word about it. The row keeps its Connect button in that state and adds
 * one line that says what to allow.
 */
export type CrmState = 'idle' | 'connecting' | 'connected' | 'blocked'
export type { CrmAccount, CrmConnections, CrmProvider }
export type RequestState = 'idle' | 'open' | 'sending' | 'sent'

export const MasterRow = ({
	label,
	desc,
	help,
	checked,
	onChange
}: {
	label: string
	desc: string
	/** One plain sentence behind a question mark. See `HelpTip`. */
	help?: string
	checked: boolean
	onChange: (next: boolean) => void
}) => (
	<div className="flex w-full items-center gap-3">
		<div className="min-w-0 flex-1">
			<div className="flex items-center gap-1.5">
				<p className="text-[16px] font-medium leading-[22px] cg-text-text-primary">{label}</p>
				{help && <HelpTip label={label} text={help} />}
			</div>
			<p className="text-[14px] leading-[20px] cg-text-text-secondary">{desc}</p>
		</div>
		<Toggle size="md" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-label={label} />
	</div>
)

export const ToggleRow = ({
	label,
	desc,
	checked,
	disabled,
	onChange
}: {
	label: string
	/** R321 (Gustavo, 13 Sep): optional; the bot pane's rows are one line. */
	desc?: string
	checked: boolean
	disabled?: boolean
	onChange: (next: boolean) => void
}) => (
	<div className="flex w-full items-center gap-3">
		<div className="min-w-0 flex-1">
			{/* R297 (Esteban, 11 Sep, dark screenshot): the row title takes primary,
			    like the master rows above the list. Title and description were both
			    secondary, so the list read as one tone in dark. */}
			<p className="text-[12px] font-medium leading-[16px] cg-text-text-primary">{label}</p>
			{desc ? <p className="text-[12px] leading-[16px] cg-text-text-secondary">{desc}</p> : null}
		</div>
		<Toggle
			size="md"
			checked={checked}
			disabled={disabled}
			onChange={(e) => onChange(e.target.checked)}
			aria-label={label}
		/>
	</div>
)

/* R134 (Esteban, 27 Aug, inspect edit): 14px of side padding, not 16px. The
   card sits inside the wizard card, so its own gutter has to read as the
   inner one. The vertical 14px was already right, so the box is even now. */
export const SettingsCard = ({ children }: { children: React.ReactNode }) => (
	<div className="flex w-full flex-col gap-3 rounded-[10px] border cg-border-border-default cg-bg-surface px-3.5 py-3.5 shadow-[0px_1px_1px_0px_rgba(16,24,40,0.05)]">
		{children}
	</div>
)

/* R254: the grey inset that holds a rule list inside a SettingsCard. One source,
   so the email rules (EmailStep) and the bot switches (cards modal) share the
   same box: canvas fill, 8px radius, cloudwork's `py-1 pl-3.5 pr-2` gutter with
   the list's own `py-2` inside it. */
export const InsetList = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
	<div className={`rounded-lg cg-bg-canvas py-1 pl-3.5 pr-2 ${className}`}>
		<div className="flex flex-col gap-3 py-2">{children}</div>
	</div>
)

/**
 * The one close control of this lane. Every surface that closes uses this, so a
 * dialog stacked over the drawer draws the same X at the same size.
 *
 * The DS `ModalHeader` draws its own close when it gets `onClose`, but that one
 * is hardcoded at a 12px X in a 24px box, with no size axis. Beside a 20px title
 * it reads as a speck. `IconButton` is the DS control that really sizes its
 * icon: `iconSize` clones the glyph with real width and height props, so this
 * gives a true 20px X in a 32px target.
 *
 * `AppIconButton` is the wrong vehicle here and a Playwright probe proved it.
 * It drops the icon into a fixed 16px flex box and sets no size on the svg, so
 * an untitled-ui glyph keeps its intrinsic 24px height while its width clamps
 * to 16: a 16x24 cross. Both gaps are reported to the DS.
 */
export const CloseButton = ({
	onClick,
	className,
	size = 'md'
}: {
	onClick: () => void
	className?: string
	/**
	 * `md` (default): a 20px X in a 32px target, for the drawer, whose title
	 * is 20px. `sm`: a 16px X in a 28px target, for the confirm dialogs, whose
	 * `ModalHeader size="sm"` title is smaller. R148 (Esteban, 28 Aug): the
	 * 20px X read too big on the Request a new CRM integration dialog. 16px is
	 * the glyph the wizard's own close draws, so the two stacked surfaces agree.
	 */
	size?: 'sm' | 'md'
}) => (
	<IconButton
		type="button"
		variant="ghost"
		// `secondary` is the token the DS ModalHeader close already used.
		// The default `gray` is text-tertiary and reads too faint at 20px.
		color="secondary"
		size={size}
		// `iconSize` is the only path that makes the glyph truly square. The
		// size scale sets the icon BOX to 20px, but an untitled-ui svg keeps
		// its intrinsic 24px height, so the mark comes out 20x24. `iconSize`
		// clones the element with real width and height props. Measured.
		iconSize={size === 'sm' ? 16 : 20}
		aria-label="Close"
		icon={<XClose />}
		onClick={onClick}
		className={className}
	/>
)

/**
 * The width every confirm in this lane shares.
 *
 * R94: 480px let a one-line question run the full width of a laptop dialog, so
 * the eye travelled further than the words were worth, and the two buttons sat
 * marooned in the corner. 420px still holds the widest row ("Cancel" plus
 * "Authorize and connect") with room to spare. The value lives here because
 * four dialogs across three files carry it, and the next change must be one
 * edit. Write the class as a plain literal: Tailwind reads source as text, so
 * a value built with a template literal compiles to nothing.
 */
export const CONFIRM_MODAL_WIDTH = '!w-[420px]'

/** The DS title row, plus the one close control. */
export const DialogHeader = ({
	title,
	subtitle,
	onClose,
	size = 'sm'
}: {
	title: string
	subtitle?: string
	onClose: () => void
	/**
	 * R332 (Esteban, 14 Sep): "just a level smaller". `sm` is the smallest DS
	 * ModalHeader size (Text xl, 20/26). `xs` keeps that header and steps the
	 * h2 down one DS type step, Text lg (18px), through an arbitrary variant,
	 * because the DS has no size below `sm` (DS gap, logged in the lane). The
	 * line is 28px, the `sm` close mark's height, so the two centres meet
	 * (the token's 24px left the title 2px high; measured). Only the cards
	 * modal passes `xs`.
	 */
	size?: 'sm' | 'xs'
}) => (
	<div className="flex items-start gap-4">
		<ModalHeader
			title={title}
			subtitle={subtitle}
			size="sm"
			className={size === 'xs' ? 'min-w-0 flex-1 [&_h2]:text-[18px] [&_h2]:leading-7' : 'min-w-0 flex-1'}
		/>
		<CloseButton size="sm" onClick={onClose} className="shrink-0" />
	</div>
)

/**
 * The in-product help mark: a question icon with one plain sentence in a
 * tooltip.
 *
 * Ricardo Acevedo, 2026-08-20 product sync: "This is one of the pages that
 * really needs documentation... so they know what they're clicking on, what
 * it's doing." Ralph set the bar at a seven-year-old, so each `text` says what
 * happens, in short words, with no ClickGuard vocabulary in it.
 *
 * The tooltip carries the same words the page docs carry, so the short answer
 * on screen and the long answer in `docs/` never disagree.
 */
export const HelpTip = ({
	label,
	text,
	tone = 'default'
}: {
	label: string
	text: string
	/**
	 * Round 47. `brand` is the mark on the brand tint banner. The grey mark
	 * measures about 2.3:1 on #EEEDFE and all but vanishes, so the banner
	 * takes the brand ink and stays on one colour family.
	 */
	tone?: 'default' | 'brand'
}) => (
	<Tooltip content={text} position="top" maxWidth={280} portal>
		<button
			type="button"
			aria-label={`What is ${label}?`}
			className={`inline-flex shrink-0 items-center rounded-full transition-colors duration-200 motion-reduce:transition-none ${
				tone === 'brand'
					? 'text-brand-tint-text hover:text-brand-tint-text-strong'
					: 'cg-text-text-tertiary hover:text-[rgb(var(--cg-text-secondary))]'
			}`}
		>
			{/* 14px, the DS small icon step (`size-3.5`). The 16px step reads as
			    a control beside a 12px caption; this one reads as a mark. */}
			<HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
		</button>
	</Tooltip>
)

/**
 * The three outcomes of a CRM field check, plus the check itself.
 *
 * The first check reports `missing` and the second reports `found`. That is
 * the real order: a user checks, reads that the field is not there yet, goes
 * to the CRM, creates it, and checks again. A demo that finds the field on the
 * first press hides the whole step the sync asked for.
 */
export type CrmFieldState = 'idle' | 'checking' | 'missing' | 'found'

export const useCrmField = (
	verify?: () => Promise<{ status: string; verification_token: string }>,
	identity?: string,
	saved?: { status: string } | null
) => {
	const [fieldState, setFieldState] = useState<CrmFieldState>('idle')
	const [verificationToken, setVerificationToken] = useState<string | null>(null)
	const [error, setError] = useState('')
	const tries = useRef(0)
	const version = useRef(0)
	const resetField = () => {
		version.current++
		tries.current = 0
		setFieldState('idle')
		setVerificationToken(null)
		setError('')
	}
	useEffect(() => {
		resetField()
		if (saved) {
			setFieldState(saved.status === 'valid' ? 'found' : 'missing')
		}
		return () => {
			version.current++
		}
	}, [identity])
	const checkField = async () => {
		const token = ++version.current
		setFieldState('checking')
		setError('')
		if (verify) {
			try {
				const result = await verify()
				if (token === version.current) {
					setVerificationToken(result.verification_token)
					setFieldState(result.status === 'valid' ? 'found' : 'missing')
				}
			} catch (e) {
				if (token === version.current) {
					setFieldState('idle')
					setError(
						e instanceof Error ? e.message : 'Unable to verify the property'
					)
				}
			}
		} else {
			tries.current++
			setFieldState(tries.current > 1 ? 'found' : 'missing')
		}
	}
	return { fieldState, checkField, resetField, verificationToken, error }
}

/**
 * The field the CUSTOMER creates, and the check that says whether it is there.
 *
 * ClickGuard writes nothing into the CRM schema. The panel names the field,
 * hands over the exact string with a copy button, and then reports what a look
 * at the CRM found. `provider` is the CRM the user just connected; the panel
 * enters when that connection lands and leaves when the user disconnects.
 */
export const CrmFieldPanel = ({
	provider,
	state,
	onCheck
}: {
	provider: CrmProvider | null
	state: CrmFieldState
	onCheck: () => void
}) => {
	const panel = useEnterExit(!!provider, { duration: 200 })
	// The provider drops to null on a disconnect, so the exit would lose its
	// name mid-fade. The last name stays held until the panel is gone.
	const held = useRef<CrmProvider | null>(null)
	if (provider) held.current = provider
	const name = provider ?? held.current

	// R222 (Esteban, 2 Sep) put Brevo / HubSpot tabs on this card, so the
	// recipe could name its CRM. R241 (Esteban, 3 Sep): "Since we can't really
	// have more than 1 crm let's not have these tabs". One CRM per form (R151),
	// so the recipe follows the connected CRM and there is nothing to pick.
	// The recipe keeps its swap: when the CRM changes while the card is up,
	// the old steps play their exit, then the new ones paint. The timer
	// matches the 150ms of `form-spam-leave`; the `--still` variant fades in
	// place, because a rise inside a card that does not move reads as a jump.
	const recipe: CrmProvider = name ?? 'brevo'
	const [painted, setPainted] = useState<CrmProvider>(recipe)
	const [leaving, setLeaving] = useState(false)
	useEffect(() => {
		if (recipe === painted) return
		setLeaving(true)
		const timer = window.setTimeout(() => {
			setPainted(recipe)
			setLeaving(false)
		}, 150)
		return () => window.clearTimeout(timer)
	}, [recipe, painted])

	if (!panel.mounted || !name) return null

	const layer = (active: boolean) =>
		`col-start-1 row-start-1 self-center transition-opacity duration-200 motion-reduce:transition-none ${
			active ? 'opacity-100' : 'pointer-events-none opacity-0'
		}`

	return (
		<div
			className={`transition-[opacity,transform] duration-200 motion-reduce:transition-none ${
				panel.entered ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'
			}`}
		>
			<SettingsCard>
				{/* The tip belongs ON the title line, the way `MasterRow` sets it. As a
				    sibling of the whole text block it landed at the end of the
				    DESCRIPTION, which is the widest line, so it read as a control for
				    the card and not for the heading it explains. */}
				<div className="min-w-0">
					{/* The button shares the TITLE line only. Beside the whole text
					    block it stole 150px from the description, which then wrapped
					    two words early for no reason. */}
					<div className="flex items-center justify-between gap-3">
						<div className="flex min-w-0 items-center gap-1.5">
							<p className="text-[16px] font-medium leading-[22px] cg-text-text-primary">
								Setup the spam flag
							</p>
							<HelpTip
								label="the CRM field"
								text="Your CRM keeps its own list of fields. We do not touch that list. You add one text field with this exact name, and we write the result of each check into it."
							/>
						</div>
					</div>
					{/* R124 (Gustavo, pins 5g5co1 and j5d4jg): the card names the
					    promise in the CRM owner's terms. We write one value per lead
					    and touch nothing else. R132: 8px above the text, because the
					    button makes the title line taller than the other cards' and
					    the text sat against its bottom edge. */}
					<p className="mt-2 text-[14px] leading-[20px] cg-text-text-secondary">
						ClickGuard will only update the field&apos;s value (e.g. invalid,
						spam-trap, etc.) for each lead. We will never edit or delete any
						other record.
					</p>
				</div>
				{/* R133 (Esteban, 27 Aug): a rule between the promise and the recipe,
				    not more space. R222 made the tab row's baseline that rule; R241
				    took the tabs out, so the plain rule is back. The card's `gap-3`
				    keeps 12px on each side of it, the same as the footer rule below
				    the steps. */}
				<Divider />
				{/* R91: this block is a recipe, not metadata. The four values are
				    what the customer types into the CRM's own new-field form, so
				    they read as four steps in the order the CRM asks for them. The
				    old row of `label: value` pairs read as facts about a thing we
				    had already made, which is the opposite of the truth.

				    R111: `StepList` draws the markers and the rail now, and it
				    numbers the rows itself. R91 gave this list an OUTLINE circle so
				    it would not claim progress; Esteban asked for the purple circle
				    on every step list in the lane, so the outline is gone. */}
				<div
					key={painted}
					className={
						leaving
							? 'form-spam-leave form-spam-leave--still'
							: 'form-spam-panel form-spam-panel--still'
					}
				>
					<StepList
						items={[
							<>
								Open{' '}
								<span className="font-medium cg-text-text-primary">{CRM_FIELDS[painted].where}</span>.
							</>,
							<>
								Pick{' '}
								<span className="font-medium cg-text-text-primary">{CRM_FIELDS[painted].object}</span>.
							</>,
							<>
								Add a field of type{' '}
								<span className="font-medium cg-text-text-primary">{CRM_FIELDS[painted].type}</span>.
							</>,
							<>
								{/* R96: the copy control moved OFF this line and INTO the box.
								    The value is one short token, so the box reads as a field,
								    and a field carries its own trailing button. The DS
								    `CodeBlock size="sm"` draws it. */}
								Name it exactly this.
								{/* Half the step, not the whole step. The value is 16 characters,
								    so a full width box promised a long value and delivered a
								    short one. The widget box below stays full width, because
								    that one really holds a long line. The floor keeps the token
								    on one line in the narrower modal card. */}
								<div className="mt-2 w-fit min-w-[200px] max-w-full">
									<CodeBlock
										size="sm"
										code={CRM_FIELDS[painted].name}
										copyLabel={`Copy ${CRM_FIELDS[painted].name}`}
									/>
								</div>
							</>,
							<>
								Save it. We write{' '}
								<span className="font-medium cg-text-text-primary">{CRM_FIELDS[painted].holds}</span> into it.
							</>,
							<>Click Verify below. We look for the field in your CRM.</>
						]}
					/>
				</div>
				<Divider />
				<div className="flex items-center justify-between gap-3">
					<div className="grid min-w-0" aria-live="polite">
						<p className={`text-[12px] leading-[16px] cg-text-text-secondary ${layer(state === 'idle')}`}>
							We have not looked for the field yet.
						</p>
						<div className={`flex items-center gap-1.5 ${layer(state === 'checking')}`}>
							<DotMatrix size="xs" label="Looking" />
							<p className="text-[12px] font-medium leading-[16px] cg-text-text-secondary">
								Looking in {CRM_NAMES[name]}…
							</p>
						</div>
						<div className={`flex items-center gap-1.5 ${layer(state === 'missing')}`}>
							<AlertCircle className="h-4 w-4 shrink-0 text-[rgb(var(--fs-warning-ink))]" aria-hidden="true" />
							<p className="text-[12px] font-medium leading-[16px] text-[rgb(var(--fs-warning-ink))]">
								Not there yet. Create it, then check again.
							</p>
						</div>
						<div className={`flex items-center gap-1.5 ${layer(state === 'found')}`}>
							<CheckCircle className="h-4 w-4 shrink-0 cg-text-success" aria-hidden="true" />
							<p className="text-[12px] font-medium leading-[16px] cg-text-success">
								Found. We will write the result into it.
							</p>
						</div>
					</div>
					<AppButton
						type="button"
						variant="outline"
						size="sm"
						// The label holds through the check. It swaps only once a result is
						// in, so the button does not change width under the cursor the
						// moment the user presses it.
						label={state === 'idle' || state === 'checking' ? 'Verify' : 'Check again'}
						disabled={state === 'checking'}
						onClick={onCheck}
					/>
				</div>
			</SettingsCard>
		</div>
	)
}

/*
 * The retention banner is GONE (2026-08-25, his call: "Get rid of this").
 *
 * It was the `brand` Banner under the email rules: the ClockRewind mark, "We
 * keep a copy of each address.", and a 30 days / 90 days / 12 months
 * `SegmentedTabs`. R81 had already taken it out of the link wizard; this
 * removes the last copy, on the drawer's Setup tab, so `RetentionPanel` has no
 * caller left and the component is deleted with it.
 *
 * The DATA stays. `RETENTIONS`, `RetentionKey` and `DEFAULT_RETENTION` still
 * live in `form-spam-data.tsx`, and every form still carries `retention: '90d'`,
 * so the Traffic tab keeps printing the period it holds. Only the control and
 * its disclosure banner are gone. Put the panel back from git history if he
 * ever wants the customer to set the period again.
 */

/**
 * The two CRM marks, inline. R221 (Esteban, 2 Sep): Brevo took the Salesforce
 * row ("Salesforce will be a huge one so we won't prioritize it").
 *
 * Round 44. They were `<img src="/form-spam/*.svg">`, and they were the ONLY
 * network fetch on this card, so the tiles sat empty while every other part of
 * the row had already painted from the bundle. Measured on the dev server with
 * 60 curls while a source file changed: 2 to 20 ms when it is idle, and 1.59
 * SECONDS during the rebuild, because webpack-dev-middleware queues every
 * request, `public/` files included, until the compile ends. The server also
 * sends `Cache-Control: public, max-age=0`, so the browser re-requests on every
 * page load. Both marks are one path and under 700 bytes, so the bundle carries
 * them for nothing and the tile is never empty.
 *
 * `aria-hidden`, because the row prints the name next to the tile.
 */
export const BrevoMark = () => (
	<svg viewBox="0 0 24 24" className="h-6 w-6" fill="#0B996E" aria-hidden="true" focusable="false">
		<path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zM7.2 4.8h5.747c2.34 0 3.895 1.406 3.895 3.516 0 1.022-.348 1.862-1.09 2.588C17.189 11.812 18 13.22 18 14.785c0 2.86-2.64 5.016-6.164 5.016H7.199v-15zm2.085 1.952v5.537h.07c.233-.432.858-.796 2.249-1.226 2.039-.659 3.037-1.52 3.037-2.655 0-.998-.766-1.656-1.924-1.656H9.285zm4.87 5.266c-.766.385-1.67.748-2.76 1.11-1.229.387-2.11 1.386-2.11 2.407v2.315h2.365c2.387 0 4.149-1.34 4.149-3.155 0-1.067-.625-2.087-1.645-2.677z" />
	</svg>
)

export const HubSpotMark = () => (
	<svg viewBox="0 0 24 24" className="h-6 w-6" fill="#FF7A59" aria-hidden="true" focusable="false">
		<path d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.067A2.2 2.2 0 0017.238.845h-.067a2.2 2.2 0 00-2.193 2.193v.067a2.196 2.196 0 001.252 1.973l.013.006v2.852a6.22 6.22 0 00-2.969 1.31l.012-.01-7.828-6.095A2.497 2.497 0 104.3 4.656l-.012.006 7.697 5.991a6.176 6.176 0 00-1.038 3.446c0 1.343.425 2.588 1.147 3.607l-.013-.02-2.342 2.343a1.968 1.968 0 00-.58-.095h-.002a2.033 2.033 0 102.033 2.033 1.978 1.978 0 00-.1-.595l.005.014 2.317-2.317a6.247 6.247 0 104.782-11.134l-.036-.005zm-.964 9.378a3.206 3.206 0 113.215-3.207v.002a3.206 3.206 0 01-3.207 3.207z" />
	</svg>
)

/** The sentinel value of the last account-list entry. Never an account id. */
const NEW_CRM_ACCOUNT = '__connect-new__'

export interface CrmAccountDraft {
	id?: string
	/** R285 (Esteban, 7 Sep): the organization, never a person. */
	name: string
	email: string
	/** R285: the id the CRM shows for the account, when the login carries one. */
	accountId?: string
}

export const CrmRow = ({
	tile,
	tileClass = 'cg-border-border-default cg-bg-canvas',
	name,
	desc,
	action,
	disabled = false,
	state = 'idle',
	busyLabel = 'Connecting…',
	doneLabel = 'Connected',
	actionSelected = false,
	actionControls,
	onAction,
	accounts,
	accountId = null,
	accountLabel,
	connectNewLabel = 'Connect a new account',
	onPickAccount,
	onConnectNew
}: {
	tile: React.ReactNode
	tileClass?: string
	name: string
	desc?: string
	action: string
	state?: CrmState
	disabled?: boolean
	busyLabel?: string
	doneLabel?: string
	actionSelected?: boolean
	actionControls?: string
	onAction?: () => void
	/**
	 * Gustavo, 2026-08-26: once the workspace holds a login for this CRM, the
	 * subtitle becomes this account list and the Connect button goes. The
	 * list ends with a "connect a new account" entry that runs `onConnectNew`.
	 */
	accounts?: CrmAccount[]
	/** The account this form uses. `null` shows the placeholder. */
	accountId?: string | null
	/** ARIA label of the account list. */
	accountLabel?: string
	connectNewLabel?: string
	onPickAccount?: (id: string) => void
	onConnectNew?: () => void
}) => {
	// The three action states share one grid cell and crossfade, so the row
	// width never jumps.
	const layer = (active: boolean) =>
		`col-start-1 row-start-1 self-center justify-self-end transition-opacity duration-200 motion-reduce:transition-none ${
			active ? 'opacity-100' : 'pointer-events-none opacity-0'
		}`
	// The subtitle and the account list swap in one grid cell. Each side is
	// held in the DOM through its fade, so neither one hard-cuts.
	const hasAccounts = Boolean(accounts && accounts.length > 0)
	const list = useEnterExit(hasAccounts, { duration: 200 })
	// The action button belongs to the same "no account yet" state as the
	// account list, so it leaves when the list arrives. It is NOT tied to the
	// subtitle: Gustavo dropped the CRM subtitles on 2026-09-16, and the button
	// must stay on a row that carries none.
	const act = useEnterExit(!hasAccounts, { duration: 200 })
	const sub = useEnterExit(!hasAccounts && Boolean(desc), { duration: 200 })
	// R153: the pop-up blocked line enters and leaves with the state.
	const blockedLine = useEnterExit(state === 'blocked', { duration: 200 })
	const canAct = state === 'idle' || state === 'blocked'
	const fade = (entered: boolean) =>
		`col-start-1 row-start-1 transition-opacity duration-200 motion-reduce:transition-none ${
			entered ? 'opacity-100' : 'pointer-events-none opacity-0'
		}`
	const accountOptions = [
		...(accounts ?? []).map((account) => ({ value: account.id, label: account.name })),
		{
			value: NEW_CRM_ACCOUNT,
			label: connectNewLabel,
			leadingIcon: <Plus className="h-4 w-4" aria-hidden="true" />
		}
	]
	return (
		<div className="flex w-full items-center gap-3">
			<div
				className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border shadow-[0px_1px_1px_0px_rgba(16,24,40,0.05)] ${tileClass}`}
			>
				{tile}
			</div>
			<div className="min-w-0 flex-1">
				<p className="truncate text-[14px] font-medium leading-[20px] cg-text-text-primary">{name}</p>
				<div className="grid grid-cols-[minmax(0,1fr)] justify-items-start">
					{sub.mounted && (
						<p
							className={`w-full truncate text-[14px] leading-[20px] cg-text-text-secondary ${fade(sub.entered)}`}
							aria-hidden={!sub.entered}
						>
							{desc}
						</p>
					)}
					{list.mounted && (
						<div className={`mt-1 min-w-0 max-w-full ${fade(list.entered)}`} aria-hidden={!list.entered}>
							<Dropdown
								// Escape the drawer and wizard overflow wrappers.
								portal
								variant="outline"
								size="sm"
								ariaLabel={accountLabel}
								placeholder="Choose an account"
								options={accountOptions}
								value={accountId ?? ''}
								onValueChange={(value) => {
									if (value === NEW_CRM_ACCOUNT) onConnectNew?.()
									else onPickAccount?.(value)
								}}
								disabled={disabled || state === 'connecting'}
							/>
						</div>
					)}
				</div>
				{blockedLine.mounted && (
					<p
						className={`mt-1 text-[12px] leading-[16px] text-[rgb(var(--fs-danger-ink))] transition-opacity duration-200 motion-reduce:transition-none ${
							blockedLine.entered ? 'opacity-100' : 'opacity-0'
						}`}
						role="status"
						aria-hidden={!blockedLine.entered}
					>
						Your browser blocked the login window. Allow pop-ups for this site, then try again.
					</p>
				)}
			</div>
			<div className="grid shrink-0" aria-live="polite">
				<div className={layer(canAct)} aria-hidden={!canAct}>
					{act.mounted && (
						<AppButton
							variant="outline"
							size="sm"
							label={action}
							onClick={onAction}
							disabled={disabled}
							selected={actionSelected}
							aria-expanded={actionControls ? actionSelected : undefined}
							aria-controls={actionControls}
							tabIndex={canAct ? undefined : -1}
						/>
					)}
				</div>
				<div className={`flex items-center gap-1.5 ${layer(state === 'connecting')}`} aria-hidden={state !== 'connecting'}>
					<DotMatrix size="xs" label={busyLabel} />
					<p className="text-[12px] font-medium leading-[16px] cg-text-text-secondary">{busyLabel}</p>
				</div>
				<div className={`flex items-center gap-1.5 ${layer(state === 'connected')}`} aria-hidden={state !== 'connected'}>
					<CheckCircle className="h-4 w-4 cg-text-success" aria-hidden="true" />
					<p className="text-[12px] font-medium leading-[16px] cg-text-success">{doneLabel}</p>
				</div>
			</div>
		</div>
	)
}

/**
 * The demo OAuth popup flow. `connectCrm` opens the popup and flips the row
 * to `connecting`; the popup posts `cg-crm-connected` back on "login", which
 * flips it to `connected` and fires `onConnected`. A popup closed without
 * the login reverts its row to idle. `reset` puts both rows back to idle.
 */
export const useCrmConnect = (
	onConnected?: (provider: CrmProvider, account: CrmAccountDraft) => void
) => {
	const { api } = useFormSpam()
	const crmApi = useMemo(
		() => (api ? createCrmApi(api.accountId) : undefined),
		[api]
	)
	const [preferenceError, setPreferenceError] = useState('')
	const liveOAuth = useHubSpotConnect(crmApi, (account) => {
		onConnected?.('hubspot', {
			id: account.id,
			name: account.name,
			email: account.credential_identity || account.name,
			accountId: account.portal_id
		})
		if (crmApi && api)
			void crmApi
				.setPreference(api.assetId, account.id)
				.catch(() =>
					setPreferenceError(
						'HubSpot connected, but the default for this website could not be saved. You can select the connection manually for new forms.'
					)
				)
	})
	const [crm, setCrm] = useState<Record<CrmProvider, CrmState>>({
		brevo: 'idle',
		hubspot: 'idle'
	})
	const popupRef = useRef<Window | null>(null)
	const popupPoll = useRef<number>()
	const onConnectedRef = useRef(onConnected)
	onConnectedRef.current = onConnected

	useEffect(() => {
		const onMessage = (e: MessageEvent) => {
			if (api) return
			if (e.origin !== window.location.origin) return
			// R153 (audit 2026-08-28): only the window we opened can connect.
			// Any same-origin frame could post the type before this guard.
			if (!popupRef.current || e.source !== popupRef.current) return
			if (e.data?.type !== 'cg-crm-connected') return
			const provider = e.data.provider as CrmProvider
			if (provider !== 'brevo' && provider !== 'hubspot') return
			window.clearInterval(popupPoll.current)
			setCrm((prev) => ({ ...prev, [provider]: 'connected' }))
			// The popup names the account it signed in to. An old popup build
			// posts none; the demo organization stands in.
			const raw = e.data.account as Partial<CrmAccountDraft> | undefined
			const account: CrmAccountDraft = {
				name: typeof raw?.name === 'string' && raw.name ? raw.name : 'Acme Inc',
				email:
					typeof raw?.email === 'string' && raw.email
						? raw.email
						: 'maya@acme.co',
				// R285: the CRM's own id for the account, when the popup sends one.
				...(typeof raw?.accountId === 'string' && raw.accountId
					? { accountId: raw.accountId }
					: {})
			}
			onConnectedRef.current?.(provider, account)
		}
		window.addEventListener('message', onMessage)
		return () => {
			window.removeEventListener('message', onMessage)
			window.clearInterval(popupPoll.current)
		}
	}, [])

	const connectCrm = (provider: CrmProvider) => {
		if (api) {
			setPreferenceError('')
			if (provider === 'hubspot') void liveOAuth.connect()
			return
		}
		const width = 480
		const height = 640
		const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2)
		const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2)
		const popup = window.open(
			`/preview/oauth/${provider}`,
			'cg-oauth',
			`width=${width},height=${height},left=${left},top=${top},popup=yes`
		)
		if (!popup) {
			// R153 (audit 2026-08-28): a blocked window used to return in silence.
			setCrm((prev) => ({ ...prev, [provider]: 'blocked' }))
			return
		}
		popupRef.current = popup
		// One popup name, one window: a second Connect NAVIGATES the first
		// window instead of opening a new one. The poll below then watches
		// this provider only, so the row that lost the window must go back to
		// idle here or it reads "Connecting…" for the rest of the session.
		setCrm((prev) => {
			const next = { ...prev, [provider]: 'connecting' as CrmState }
			;(Object.keys(next) as CrmProvider[]).forEach((key) => {
				if (key !== provider && next[key] === 'connecting') next[key] = 'idle'
			})
			return next
		})
		window.clearInterval(popupPoll.current)
		popupPoll.current = window.setInterval(() => {
			if (!popupRef.current?.closed) return
			window.clearInterval(popupPoll.current)
			setCrm((prev) =>
				prev[provider] === 'connecting' ? { ...prev, [provider]: 'idle' } : prev
			)
		}, 400)
	}

	const reset = () => {
		setCrm({ brevo: 'idle', hubspot: 'idle' })
		window.clearInterval(popupPoll.current)
		popupRef.current?.close()
	}

	return {
		crm: api
			? {
					brevo: 'idle' as CrmState,
					hubspot: liveOAuth.busy
						? ('connecting' as CrmState)
						: ('idle' as CrmState)
			  }
			: crm,
		connectCrm,
		reset,
		error: liveOAuth.error || preferenceError
	}
}

/** The words one degree asks the user to accept, or `null` if it asks none. */
export const botConsentOf = (level: BotLevel): BotConsent | null =>
	BOT_LEVELS.find((def) => def.key === level)?.consent ?? null

/**
 * True when the host must hold its own Next button until the user agrees.
 * A master that is off touches no visitor, so it asks for nothing.
 */
export const botConsentNeeded = (master: boolean, level: BotLevel) =>
	master && botConsentOf(level) !== null

/**
 * R49: the degree used to open a confirm dialog over the surface. The user now
 * agrees in place, and the host holds Next until the box is checked.
 *
 * The row sits OUTSIDE the grey picker on purpose. Inside it, a fourth square
 * under three radios reads as a fourth degree.
 *
 * DS gap, reported: `Checkbox.label` is typed `string`, so a policy link cannot
 * go in it. The box is the DS Checkbox with no label of its own, and the
 * sentence is a sibling `<label htmlFor>`. The link stays OUTSIDE that label,
 * because an anchor inside a label toggles the box when the user clicks it, and
 * React cannot stop that: it listens at the root, so `stopPropagation` never
 * reaches the native bubble.
 */
export const BotConsentRow = ({
	consent,
	checked,
	onChange
}: {
	consent: BotConsent | null
	checked: boolean
	onChange: (next: boolean) => void
}) => {
	const row = useEnterExit(!!consent, { duration: 200 })
	// The copy drops to null the moment the user leaves the degree, so the exit
	// would have no words left to draw. The last copy stays held until it goes.
	const held = useRef<BotConsent | null>(null)
	if (consent) held.current = consent
	const copy = consent ?? held.current
	const id = useId()

	if (!row.mounted || !copy) return null

	return (
		<div
			className={`flex items-start gap-2 transition-[opacity,transform] duration-200 motion-reduce:transition-none ${
				row.entered ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'
			}`}
		>
			<Checkbox
				id={id}
				size="sm"
				checked={checked}
				onChange={(e) => onChange(e.target.checked)}
			/>
			<p className="text-[12px] leading-[16px] cg-text-text-secondary">
				<label htmlFor={id} className="cursor-pointer">
					{copy.accept}, and I agree to the{' '}
				</label>
				<a
					href={copy.href}
					target="_blank"
					rel="noreferrer"
					className="font-medium text-brand-tint-text underline decoration-[#c9c5ff] underline-offset-2 transition-colors duration-200 hover:decoration-brand-tint-text motion-reduce:transition-none"
				>
					{copy.policy}
				</a>
				.
			</p>
		</div>
	)
}

/**
 * R69, the second axis. The action says WHAT we do; this says WHO it reaches.
 * The two used to be one field, so "CAPTCHA the near-certain bots only" was
 * unreachable.
 *
 * This is the whole answer to "give the impression of sensitivity without a
 * slider or that word". The dial is the target named in plain words, plus one
 * live consequence line that moves with it. There is no meter and no bar, and
 * the words "sensitivity", "aggressive" and "strict" never appear.
 */
// R278 (Esteban, 4 Sep, item 5): the live panes show "Strict" as a sensitivity label on his explicit brief; Gustavo's 2026-08-20 word ban stays for the legacy BotStep below.
const BOT_TARGETS: { key: CaptchaTrigger; label: string }[] = [
	{ key: 'high-only', label: 'Near-certain bots' },
	{ key: 'suspected', label: 'Likely bots too' },
	{ key: 'always', label: 'Anything odd' }
]

/**
 * R69: the second axis. The radios above say WHAT we do; this row says WHO it
 * reaches. The two were one field before, so "CAPTCHA the near-certain bots
 * only" could not be expressed at all.
 *
 * Words only. No meter, no bar, no slider. Gustavo rejected "aggressive" at the
 * 2026-08-20 sync because the user cannot tell what it means, and a strength
 * word carries the same fault. The estimate line under the segments is the
 * whole impression of degree, and it is a real number from the same series the
 * dashboard charts draw.
 *
 * The DS `Collapse` owns the reveal, not `useEnterExit`. This row appears
 * BETWEEN other content in the drawer, so the height has to animate or the
 * settings below it jump. `Collapse` also takes the closed subtree out of the
 * tab order, which a plain opacity fade does not.
 */
const BotTargetRow = ({
	level,
	trigger,
	estimate,
	disabled,
	onTrigger
}: {
	level: BotLevel
	trigger: CaptchaTrigger
	/** The share one target reaches, or null when no form has data yet. */
	estimate: string | null
	disabled: boolean
	onTrigger: (next: CaptchaTrigger) => void
}) => {
	// Monitor stops nobody, so it has no target to name.
	const shown = level === 'monitor' ? null : level
	// The level drops to Monitor the moment the user goes back, so the collapse
	// would have no words left to draw and the row would blank before it
	// closed. The last one stays held. Same reason as `BotConsentRow` above.
	const held = useRef<BotLevel>('challenge')
	if (shown) held.current = shown
	const copy = shown ?? held.current

	const question = copy === 'block' ? 'Who gets blocked?' : 'Who gets the CAPTCHA?'
	const consequence =
		copy === 'block'
			? `About ${estimate} of your submissions would be stopped.`
			: `About ${estimate} of your submissions would see one.`

	// The estimate line is a conditional render of its own, so it gets its own
	// enter and exit. It goes away when no form has data to count, and it must
	// not cut. The text is held for the same reason the level is: `consequence`
	// reads "About null" the moment the estimate goes, so the exit would draw
	// the word "null" as it faded.
	const line = useEnterExit(estimate !== null, { duration: 200 })
	const heldLine = useRef('')
	if (estimate !== null) heldLine.current = consequence

	return (
		// `Collapse` clips its own box, so the segment focus ring needs room to
		// the sides. The wrapper takes 8px out and the content puts 8px back, so
		// the rule and the segments keep their true width.
		<Collapse open={shown !== null} className="-mx-2">
			<div className="flex flex-col gap-3 px-2">
				{/* The parent group holds a 12px gap, and this one holds 12px too,
				    so the rule keeps the same space above it and below it. */}
				<Divider />
				<div className="flex flex-col gap-2">
					<p className="text-[12px] font-medium leading-[16px] cg-text-text-secondary">{question}</p>
					{/* Two colour overrides, both because of the #f6f6f6 group box.
					    The DS track is `cg-bg-surface-2` (242 242 242), which sits four
					    levels UNDER that box, so the two read as one flat field and the
					    control loses its shape. The track goes white and lifts out of the
					    box instead. The DS pill is white too, so it would then vanish into
					    the track: `activeTone="brand"` keeps the pill readable.
					    DS GAP: `SegmentedTabs` has a tone axis for the PILL and none for
					    the track, so a track on any near-gray surface has this fault.
					    The `!` prefix is required: the DS stylesheet loads after the app
					    utilities, so a plain `bg-white` never beats `cg-bg-surface-2`. */}
					<SegmentedTabs
						size="regular"
						activeTone="brand"
						value={trigger}
						onChange={(next) => onTrigger(next as CaptchaTrigger)}
					>
						<SegmentedTabList aria-label={question} className="!bg-[rgb(var(--cg-surface))]">
							{BOT_TARGETS.map((t) => (
								<SegmentedTab key={t.key} value={t.key} label={t.label} disabled={disabled} />
							))}
						</SegmentedTabList>
					</SegmentedTabs>
					{/* The line hides itself when the account has no traffic to count.
					    A made-up number would read as a promise. */}
					{line.mounted && (
						<p
							className={`text-[12px] leading-[16px] cg-text-text-secondary transition-opacity duration-200 motion-reduce:transition-none ${
								line.entered ? 'opacity-100' : 'opacity-0'
							}`}
							aria-live="polite"
						>
							{estimate !== null ? consequence : heldLine.current}
						</p>
					)}
				</div>
			</div>
		</Collapse>
	)
}

/**
 * The Bot Verification field group: one master toggle, then the three degrees
 * of action, one of which is always chosen.
 *
 * The two sub-toggles this replaces could stand in a state nobody asked for (a
 * block with no captcha under it), and neither of them said what the visitor
 * would meet. The degrees are single-select and each one states its own
 * consequence, so the user reads the action, not a strength word.
 *
 * R49: `onLevel` fires at once, on every degree. The user's word is no longer a
 * dialog over the surface, it is the consent row under the picker, and the host
 * holds its own Next button until the user checks that row.
 */
/**
 * R182: the card screen puts the master ON THE CARD, so the pane must be able
 * to drop its own copy of it. `showMaster` defaults to true, so every call site
 * that came before the card screen keeps the row it always had.
 */
export const BotStep = ({
	master,
	level,
	trigger,
	estimate,
	consent,
	showMaster = true,
	onMaster,
	onLevel,
	onTrigger,
	onConsent
}: {
	master: boolean
	level: BotLevel
	/** R69: WHO the action reaches. Independent of the action itself. */
	trigger: CaptchaTrigger
	/** The share that target reaches, or null when no form has data yet. */
	estimate: string | null
	consent: boolean
	showMaster?: boolean
	onMaster: (next: boolean) => void
	onLevel: (next: BotLevel) => void
	onTrigger: (next: CaptchaTrigger) => void
	onConsent: (next: boolean) => void
}) => (
	<SettingsCard>
		{showMaster && (
			<MasterRow
				label="Bot verification"
				desc="Scores each submission for bot activity before it goes through."
				help="A robot can fill your form thousands of times. We give each submission a score from 0 to 100 for how much it looks like a robot. You choose what we do with a high score."
				checked={master}
				onChange={onMaster}
			/>
		)}
		<div
			className={`flex flex-col gap-3 rounded-lg cg-bg-canvas px-3.5 py-3 transition-opacity duration-200 motion-reduce:transition-none ${
				master ? '' : 'pointer-events-none opacity-40'
			}`}
		>
			<div className="flex items-center gap-1.5">
				<p className="text-[12px] font-medium leading-[16px] cg-text-text-secondary">
					What happens when the score says bot
				</p>
				<HelpTip
					label="the degree"
					text="Three choices, from soft to hard. We can just write it down, we can ask the visitor to prove they are a person, or we can stop the form. The harder you go, the more robots you stop, and the more real people you can lose."
				/>
			</div>
			{/* The group holds the 12px rhythm of the card on its own, so every
			    rule keeps the same space above it and below it. */}
			<RadioGroup
				size="sm"
				value={level}
				onValueChange={(next) => onLevel(next as BotLevel)}
				disabled={!master}
				aria-label="What happens when the score says bot"
			>
				{BOT_LEVELS.map((def, i) => (
					<React.Fragment key={def.key}>
						{i > 0 && <Divider />}
						<Radio value={def.key} label={def.label} description={def.desc} />
					</React.Fragment>
				))}
			</RadioGroup>
			<BotTargetRow
				level={level}
				trigger={trigger}
				estimate={estimate}
				disabled={!master}
				onTrigger={onTrigger}
			/>
		</div>
		<BotConsentRow
			consent={master ? botConsentOf(level) : null}
			checked={consent}
			onChange={onConsent}
		/>
	</SettingsCard>
)

/**
 * R85: the widget check.
 *
 * Step 3 asked "I added this to my form." with a checkbox. A checkbox is a
 * claim, and the list then reads that claim as protection. The check asks the
 * page instead, and it reports in the SAME three states the form check on step
 * 1 reports in, so the wizard answers one question one way.
 *
 * The demo rule is `useCrmField`'s, copied: the first look misses, the next one
 * finds. That is also what really happens, because the user pastes the snippet
 * and then has to publish the page.
 *
 * The check does NOT gate Next. A user who pastes the snippet later must still
 * be able to finish the wizard, and a form without the widget already reads as
 * "Needs setup" in the list (R69 widened `needsSetup` for that).
 */
export type WidgetCheckState = 'idle' | 'checking' | 'missing' | 'found'

export const useWidgetCheck = (
	verify?: () => Promise<boolean>,
	identity?: string
) => {
	const [widgetState, setWidgetState] = useState<WidgetCheckState>('idle')
	const tries = useRef(0)
	const timer = useRef<number>()
	useEffect(() => () => window.clearTimeout(timer.current), [])

	const generation = useRef(0)
	useEffect(() => {
		generation.current++
		setWidgetState('idle')
		return () => {
			generation.current++
		}
	}, [identity])
	const checkWidget = () => {
		if (verify) {
			const token = ++generation.current
			setWidgetState('checking')
			void verify()
				.then((found) => {
					if (token === generation.current)
						setWidgetState(found ? 'found' : 'missing')
				})
				.catch(() => {
					if (token === generation.current) setWidgetState('missing')
				})
			return
		}
		tries.current += 1
		const found = tries.current > 1
		setWidgetState('checking')
		window.clearTimeout(timer.current)
		timer.current = window.setTimeout(
			() => setWidgetState(found ? 'found' : 'missing'),
			1100
		)
	}

	const resetWidget = () => {
		generation.current++
		window.clearTimeout(timer.current)
		tries.current = 0
		setWidgetState('idle')
	}

	return { widgetState, checkWidget, resetWidget }
}

/**
 * R69, the widget install group. It opens step 3, because the widget is the
 * only part of Form Spam the visitor actually meets: the protection below it
 * decides nothing until the widget is on the page.
 *
 * The snippet is NEW, and it is NOT the one on the setup page. That one is the
 * site-wide ClickGuard tag (`trackingScript`), and its own comment says there
 * is no second forms.js to install. This one names ONE form, so the agent knows
 * which set of rules to run. Only the presentation is shared, and R97 moved it
 * into the DS `CodeBlock size="sm"`, so the two boxes now agree without any
 * stylesheet. R154 (audit 2026-08-28) removed the dead `.form-spam-code` rule
 * from setup-accordion.css and its copy in link-form-modal.css.
 */
/**
 * The preview is a PICTURE of the widget, not the widget. Its Privacy and Help
 * links must not navigate: a click would drop the user out of the wizard, in
 * the middle of a form, onto a marketing page.
 *
 * DS GAP TO REPORT, do not patch: `VerificationShield` types `privacyUrl` and
 * `helpUrl` as `string | null`, and `null` HIDES the link. There is no way to
 * print the words and refuse the click. It needs an `interactive={false}` axis.
 *
 * So the caller takes the href off. An `<a>` with no `href` keeps every hover
 * rule the DS wrote, because those are plain `hover:` classes and they do not
 * read the attribute. It loses the navigation, the pointer cursor and the tab
 * stop. React never writes back an attribute it did not change, so one layout
 * effect holds for the life of the node.
 */
const useInertPreviewLinks = () => {
	const ref = useRef<HTMLDivElement>(null)
	useLayoutEffect(() => {
		const host = ref.current
		if (!host) return
		host.querySelectorAll('a[href]').forEach((link) => {
			link.removeAttribute('href')
			link.removeAttribute('target')
			link.removeAttribute('rel')
		})
	}, [])
	return ref
}

export const WidgetStep = ({
	action,
	state,
	onCheck
}: {
	/** R280 (Esteban, 4 Sep, item 3): the `data-action` value, the form's slug without `#`. Omair's SDK markup. */
	action: string
	state: WidgetCheckState
	onCheck: () => void
}) => {
	const { setup, api } = useFormSpam()
	const [showSecret, setShowSecret] = useState(false)
	const previewRef = useInertPreviewLinks()
	// R280 (Esteban, 4 Sep, item 3): Omair's SDK markup. One div with the SDK
	// class and `data-action`; no site key, no selector. R293 (Gustavo, 4 Sep,
	// pin #79): the placement sentence left the box for the head's second
	// line, so the copied snippet is the one line the page needs.
	const snippet = `<div class="clickguard-captcha" data-action="${action}"></div>`

	// The four states share one grid cell, so the row keeps its height and the
	// button never moves sideways as the words change. Copied from
	// `CrmFieldPanel` above, which asks the same kind of question.
	const layer = (active: boolean) =>
		`col-start-1 row-start-1 self-center transition-opacity duration-200 motion-reduce:transition-none ${
			active ? 'opacity-100' : 'pointer-events-none opacity-0'
		}`

	return (
		<SettingsCard>
			<div className="flex flex-col gap-3">
				{/* R97: the copy control left this line and went INTO the box, so
				    this line only names the box now. Every code box in the lane
				    carries its own button, in the same place. The DS
				    `CodeBlock size="sm"` draws it. */}
				{/* R293 (Gustavo, 4 Sep, pins #79, #80): the help mark is gone, and
				    the placement sentence, once the snippet's comment line, is the
				    head's second line, the same two-line head "Widget preview" has. */}
				<div className="flex flex-col gap-0.5">
					<p className="text-[12px] font-medium leading-[16px] cg-text-text-secondary">
						Add the widget to your form
					</p>
					<p className="text-[12px] leading-[16px] cg-text-text-secondary">
						Paste it inside your form, just above the submit button.
					</p>
				</div>
				<CodeBlock
					size="sm"
					code={snippet}
					copyLabel="Copy the widget snippet"
				/>
				{/* R280: the SDK's fixed width and its class, so the user knows how
				    the box sits in the form and what to style. */}
				<p className="text-[12px] leading-[16px] cg-text-text-secondary">
					The widget is 360px wide and centres itself in the form. Style it with
					the{' '}
					<code className="font-mono text-[11px] cg-text-text-secondary">
						.clickguard-captcha
					</code>{' '}
					class.
				</p>
				<div className="flex items-center justify-between gap-3">
					{/* R321 (Gustavo, 13 Sep, verify step): no idle line; the cell stays
					    empty until the first check, and the other three layers keep
					    the row's height. The lines say Verify, like the button. */}
					<div className="grid min-w-0" aria-live="polite">
						<div
							className={`flex items-center gap-1.5 ${layer(
								state === 'checking'
							)}`}
						>
							<DotMatrix size="xs" label="Looking" />
							<p className="text-[12px] font-medium leading-[16px] cg-text-text-secondary">
								Looking at your page…
							</p>
						</div>
						<div
							className={`flex items-center gap-1.5 ${layer(
								state === 'missing'
							)}`}
						>
							<AlertCircle
								className="h-4 w-4 shrink-0 text-[rgb(var(--fs-warning-ink))]"
								aria-hidden="true"
							/>
							<p className="text-[12px] font-medium leading-[16px] text-[rgb(var(--fs-warning-ink))]">
								Verification widget not found in your form.
							</p>
						</div>
						<div
							className={`flex items-center gap-1.5 ${layer(
								state === 'found'
							)}`}
						>
							<CheckCircle
								className="h-4 w-4 shrink-0 cg-text-success"
								aria-hidden="true"
							/>
							<p className="text-[12px] font-medium leading-[16px] cg-text-success">
								Success
							</p>
						</div>
					</div>
					<AppButton
						type="button"
						variant="outline"
						size="sm"
						// Same rule as `CrmFieldPanel`: the label holds through the check,
						// so the button keeps its width under the cursor.
						label={
							state === 'idle' || state === 'checking'
								? 'Verify'
								: 'Verify again'
						}
						disabled={state === 'checking'}
						onClick={onCheck}
					/>
				</div>
			</div>
			{api && setup && (
				<>
					<Divider />
					<div className="flex flex-col gap-3">
						<p className="text-[12px] font-medium leading-[16px] cg-text-text-secondary">
							Server-side verification
						</p>
						<p className="text-[12px] leading-[16px] cg-text-text-secondary">
							For full protection, your server must verify every submission at{' '}
							{setup.siteverify_url} and reject it when success is false. Read
							cg-token and cg-submission-id from the submitted form. Set the
							expected form identifier on your server; do not trust one supplied
							by the visitor. Email deliverability is report-only.
						</p>
						<CodeBlock
							size="sm"
							code={JSON.stringify(
								{
									secret: 'YOUR_SERVER_SECRET',
									response: 'VALUE_OF_CG_TOKEN',
									form: action,
									submission_id: 'VALUE_OF_CG_SUBMISSION_ID',
									email: 'SUBMITTED_EMAIL'
								},
								null,
								2
							)}
							copyLabel="Copy server verification example"
						/>
						{setup.can_write && setup.secret && (
							<>
								<AppButton
									size="sm"
									variant="outline"
									label={
										showSecret ? 'Hide server secret' : 'Show server secret'
									}
									onClick={() => setShowSecret(!showSecret)}
								/>
								{showSecret && (
									<div className="ph-no-capture">
										<CodeBlock
											size="sm"
											code={setup.secret}
											copyLabel="Copy server secret"
										/>
									</div>
								)}
								<p className="text-xs cg-text-text-secondary">
									Keep this secret on your server. Never place it in your page
									HTML.
								</p>
							</>
						)}
						{setup.help_url && (
							<a
								className="text-xs cg-text-primary"
								href={setup.help_url}
								target="_blank"
								rel="noreferrer"
							>
								Setup guide
							</a>
						)}
					</div>
				</>
			)}
			<Divider />
			<div className="flex flex-col gap-2">
				<div className="flex flex-col gap-0.5">
					<p className="text-[12px] font-medium leading-[16px] cg-text-text-secondary">
						Widget preview
					</p>
					<p className="text-[12px] leading-[16px] cg-text-text-secondary">
						This is how the widget shows inside your form, right above the
						submit button.
					</p>
				</div>
				{/* R79: no cage. It was a `--cg-surface-subtle` (#fafafa) box around a
				    widget filled with `--cg-canvas` (#f6f6f6). Two greys four levels
				    apart read as ONE surface, and the widget sat short of the box on
				    every side. The widget carries its own radius and hairline, so it
				    IS the preview surface, the way it sits on a real page: grey on
				    the white card, edge to edge.

				    DS GAP TO REPORT, do not patch: `VerificationShield` is a fixed
				    `cg-w-[360px]` with no width axis. The override has to be a child
				    selector on this wrapper. `className` will not do it, because
				    `twMerge` does not know the `cg-` prefix and keeps both widths,
				    and `style` will not do it either, because the component spreads
				    `{...rest}` AFTER its own `style={borderStyle}` and a style prop
				    would delete its border. */}
				<div ref={previewRef} className="[&>[data-cg-vs]]:w-full">
					<VerificationShield duration={2600} />
				</div>
			</div>
		</SettingsCard>
	)
}

/**
 * The Email validation field group: master toggle plus the 8 rules. Both
 * surfaces show the full list, and both scroll it from the outside: the drawer
 * scrolls its own body, and R80 gave the wizard `.lfm-sheet`.
 *
 * The wizard used to cap the list at 168px behind a scroll fade. R69 removed
 * that cap. A capped list inside a scrolling body is two scrollbars in one
 * step: the wheel over the list does not move the step until the list ends.
 */
/**
 * R182: the card screen puts the master ON THE CARD, so the pane must be able
 * to drop its own copy of it. `showMaster` defaults to true, so every call site
 * that came before the card screen keeps the row it always had.
 */
export const EmailStep = ({
	master,
	rules,
	showMaster = true,
	title,
	onMaster,
	onRule
}: {
	master: boolean
	rules: Record<string, boolean>
	showMaster?: boolean
	/** R333 (Gustavo, 14 Sep, Cherry pin #102): a card title over the rules
	    for a host that drops the master row. The setup pane passes one; the
	    call sites that keep the master row pass none and look as before. */
	title?: string
	onMaster: (next: boolean) => void
	onRule: (key: string, next: boolean) => void
}) => {
	return (
		<SettingsCard>
			{title ? <p className="text-[16px] font-medium leading-[22px] cg-text-text-primary">{title}</p> : null}
			{showMaster && (
				<MasterRow
					label="Email validation"
					desc="Confirms the address on the form is a real, reachable inbox."
					help="We check that the email address can receive mail. A made-up address, or one that bounces back, gets a flag before it reaches your inbox."
					checked={master}
					onChange={onMaster}
				/>
			)}
			<InsetList
				className={`transition-opacity duration-200 motion-reduce:transition-none ${
					master ? '' : 'pointer-events-none opacity-40'
				}`}
			>
				{EMAIL_RULES.map((rule, i) => (
					<div key={rule.key} className="flex flex-col gap-3">
						{i > 0 && <Divider />}
						<ToggleRow
							label={rule.label}
							desc={rule.desc}
							checked={rules[rule.key]}
							disabled={!master}
							onChange={(next) => onRule(rule.key, next)}
						/>
					</div>
				))}
			</InsetList>
		</SettingsCard>
	)
}

/**
 * The CRM field group: the two sources, the request row and the knowledge
 * line. The modal's chrome carries the step title, so `showHeader` is for
 * the drawer card, which has no chrome above it.
 */
export const CrmStep = ({
	crm,
	accounts,
	picked,
	request,
	showHeader = false,
	fieldProvider = null,
	fieldState = 'idle',
	onConnect,
	onPickAccount,
	onRequest,
	onCheckField
}: {
	/** The popup flow's transient state per CRM. */
	crm: Record<CrmProvider, CrmState>
	/** Every login the workspace holds. The rows filter by provider. */
	accounts: CrmAccount[]
	/** The account id this form uses per CRM. */
	picked: CrmConnections
	request: RequestState
	showHeader?: boolean
	/** The connected CRM the field panel names. `null` hides the panel. */
	fieldProvider?: CrmProvider | null
	fieldState?: CrmFieldState
	onConnect: (provider: CrmProvider) => void
	onPickAccount: (provider: CrmProvider, id: string) => void
	/** Opens the request dialog. */
	onRequest: () => void
	onCheckField?: () => void
}) => {
	// A pick wins over the popup state: a form that holds an account reads
	// connected on first open, and a new login in flight reads connecting.
	const rowState = (provider: CrmProvider): CrmState =>
		crm[provider] === 'connecting' || crm[provider] === 'blocked'
			? crm[provider]
			: picked[provider]
			? 'connected'
			: 'idle'
	const accountsOf = (provider: CrmProvider) =>
		accounts.filter((account) => account.provider === provider)
	return (
	<div className="flex flex-col gap-4">
		<SettingsCard>
			{showHeader && (
				<div className="min-w-0">
					<p className="text-[16px] font-medium leading-[22px] cg-text-text-primary">
						Send clean leads to your CRM
					</p>
					<p className="text-[14px] leading-[20px] cg-text-text-secondary">
						Connect the tools you already use. Only submissions that pass the checks come through.
					</p>
				</div>
			)}
			<CrmRow
				tile={<BrevoMark />}
				tileClass="border-[#c3e6d6] bg-[#eaf7f1]"
				name="Brevo"
				action="Connect"
				state={rowState('brevo')}
				accounts={accountsOf('brevo')}
				accountId={picked.brevo}
				accountLabel="Brevo account"
				connectNewLabel="Connect a new Brevo account"
				onPickAccount={(id) => onPickAccount('brevo', id)}
				onConnectNew={() => onConnect('brevo')}
				onAction={() => onConnect('brevo')}
			/>
			<Divider />
			<CrmRow
				tile={<HubSpotMark />}
				tileClass="border-[#ffd9ce] bg-[#fff2ee]"
				name="HubSpot"
				action="Connect"
				state={rowState('hubspot')}
				accounts={accountsOf('hubspot')}
				accountId={picked.hubspot}
				accountLabel="HubSpot account"
				connectNewLabel="Connect a new HubSpot account"
				onPickAccount={(id) => onPickAccount('hubspot', id)}
				onConnectNew={() => onConnect('hubspot')}
				onAction={() => onConnect('hubspot')}
			/>
			<Divider />
			<CrmRow
				tile={<AnnotationPlus className="cg-text-brand h-6 w-6" aria-hidden="true" />}
				name="Request a new CRM integration"
				action="Request"
				state={request === 'sending' ? 'connecting' : request === 'sent' ? 'connected' : 'idle'}
				busyLabel="Sending…"
				doneLabel="Request sent"
				onAction={onRequest}
			/>
		</SettingsCard>
		{/* The field step only exists after a CRM connects, so it enters with
		    the connection and leaves with it. */}
		<CrmFieldPanel
			provider={fieldProvider}
			state={fieldState}
			onCheck={onCheckField ?? (() => undefined)}
		/>
		<p className="text-[12px] leading-[16px] cg-text-text-secondary">
			Why should I connect my CRM? Visit the{' '}
			{/* R153 (audit 2026-08-28): this was a button with no handler. */}
			<a
				href={FORM_SPAM_KB_URL}
				target="_blank"
				rel="noopener noreferrer"
				className="cg-text-brand font-medium hover:underline"
			>
				Knowledge base
			</a>
		</p>
	</div>
	)
}

/**
 * The request-a-source ask. Rendered as a SIBLING of the host surface, never
 * a child: the DS Modal renders in place and a transformed ancestor (the
 * link modal's card, the drawer's sheet) would trap this one's fixed
 * overlay. zIndex 70 beats the link modal's 60 and the sheet's 50.
 */
export const RequestSourceDialog = ({
	open,
	value,
	onChange,
	onClose,
	onSend
}: {
	open: boolean
	value: string
	onChange: (next: string) => void
	onClose: () => void
	onSend: () => void
}) => {
	const modal = useEnterExit(open, { duration: 200 })
	const inputRef = useRef<HTMLInputElement>(null)

	// Focus lands in the field once the dialog has entered, not mid-motion.
	useEffect(() => {
		if (!open) return
		const timer = window.setTimeout(
			() => inputRef.current?.focus({ preventScroll: true }),
			220
		)
		return () => window.clearTimeout(timer)
	}, [open])

	if (!modal.mounted) return null

	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			rounded="xl"
			zIndex={70}
			className={`${CONFIRM_MODAL_WIDTH} !animate-none transition-[opacity,transform] duration-200 motion-reduce:transition-none ${
				modal.entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
			}`}
			overlayClassName={`!animate-none transition-opacity duration-200 motion-reduce:transition-none ${
				modal.entered ? 'opacity-100' : 'opacity-0'
			}`}
		>
			<form
				className="flex flex-col gap-5 p-6"
				onSubmit={(e) => {
					e.preventDefault()
					onSend()
				}}
			>
				<DialogHeader
					title="Request a new CRM integration"
					subtitle="Tell us which CRM you'd like to connect and we'll take it into account."
					onClose={onClose}
				/>
				<Input
					ref={inputRef}
					label="Which CRM do you use?"
					size="md"
					state="default"
					placeholder="Pipedrive, Zoho, Close…"
					value={value}
					onChange={(e) => onChange(e.target.value)}
				/>
				<div className="flex justify-end gap-3">
					<AppButton
						type="button"
						variant="secondary"
						size="md"
						label="Cancel"
						onClick={onClose}
					/>
					<AppButton
						type="submit"
						variant="primary"
						size="md"
						label="Send request"
						disabled={!value.trim()}
					/>
				</div>
			</form>
		</Modal>
	)
}

/**
 * The words for the CRM ask. Ralph, product sync 2026-08-20: the connection
 * moves the lead off ClickGuard, so it takes the same authorization as an
 * action on a visitor. He accepts that it repeats the OAuth screen on purpose:
 * "I fully expect that they do not know. But the thing is, I'd rather let that
 * responsibility and that authorization of what either was or not done be
 * fully on them."
 *
 * R247 (Esteban, 3 Sep): "We do not need this modal - just go straight to
 * oAuth". Connect opens the OAuth popup at once; `crmAuthorizeCopy` has no
 * caller since R247 and stays for the record of Ralph's ask.
 */
export const CRM_NAMES: Record<CrmProvider, string> = {
	brevo: 'Brevo',
	hubspot: 'HubSpot'
}

export const crmAuthorizeCopy = (provider: CrmProvider): AuthorizeCopy => ({
	title: `Send your leads to ${CRM_NAMES[provider]}?`,
	// R68: two lines. "You authorize the transfer" only repeated the confirm
	// button, so it went. What the user cannot learn elsewhere stays: what
	// leaves ClickGuard, and that the connection is reversible.
	body: `Every clean submission goes to ${CRM_NAMES[provider]}, name and email included. You can disconnect at any time.`,
	confirm: 'Authorize and connect'
})

/**
 * The words for the pause ask.
 *
 * R114, at his ask: "should trigger a confirmation modal (when toggling off
 * from table as well)". The drawer header button and the forms-table Status
 * toggle both stop the checks on a form, and both take this ask first.
 *
 * `useAuthorization` below says an OFF direction never needs a word, because
 * turning a bot degree off stops us acting on a visitor. This one is the
 * exception, and it is the opposite case: a pause does not stop us acting, it
 * lets everything through. The form keeps taking submissions, unchecked, until
 * somebody notices. That is worth one stop.
 *
 * The body says what happens and that it is reversible. Nothing else: the
 * title already carries the form name.
 */
export const pauseProtectionCopy = (name: string): AuthorizeCopy => ({
	title: `Pause protection on “${name}”?`,
	body: 'New submissions reach you unchecked while the form is paused. Nothing is deleted, and you can resume at any time.',
	confirm: 'Pause protection'
})

/** One ask: the words, plus the change that runs once the user agrees. */
export interface AuthorizeAsk extends AuthorizeCopy {
	onConfirm: () => void
}

/**
 * The gate in front of every setting that acts on somebody: a bot check that
 * challenges or blocks a visitor, and a CRM that receives the lead.
 *
 * It asks EVERY time the action goes live, and it never remembers a previous
 * yes. A user who leaves a level and returns to it is authorizing that action
 * a second time, which is the whole point of the ask. Turning an action OFF
 * passes `null` and runs at once: nobody needs a word to stop us.
 */
export const useAuthorization = () => {
	const [ask, setAsk] = useState<AuthorizeAsk | null>(null)
	const authorize = (copy: AuthorizeCopy | null, act: () => void) => {
		if (!copy) {
			act()
			return
		}
		setAsk({ ...copy, onConfirm: act })
	}
	return { ask, authorize, closeAsk: () => setAsk(null) }
}

/**
 * The authorization ask itself. Same siting rules as RequestSourceDialog: a
 * SIBLING of the host surface, never a child, and zIndex 70 over the wizard's
 * 60 and the sheet's 50.
 */
export const AuthorizeDialog = ({
	ask,
	onClose
}: {
	ask: AuthorizeAsk | null
	onClose: () => void
}) => {
	const modal = useEnterExit(!!ask, { duration: 200 })
	// The ask drops to null the moment the user answers, so the exit would have
	// no words left to draw. The last ask stays held until the card is gone.
	const held = useRef<AuthorizeAsk | null>(null)
	if (ask) held.current = ask
	const copy = ask ?? held.current

	if (!modal.mounted || !copy) return null

	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			rounded="xl"
			zIndex={70}
			className={`${CONFIRM_MODAL_WIDTH} !animate-none transition-[opacity,transform] duration-200 motion-reduce:transition-none ${
				modal.entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
			}`}
			overlayClassName={`!animate-none transition-opacity duration-200 motion-reduce:transition-none ${
				modal.entered ? 'opacity-100' : 'opacity-0'
			}`}
		>
			<div className="flex flex-col gap-5 p-6">
				<DialogHeader title={copy.title} subtitle={copy.body} onClose={onClose} />
				<div className="flex justify-end gap-3">
					<AppButton
						type="button"
						variant="secondary"
						size="md"
						label="Cancel"
						onClick={onClose}
					/>
					<AppButton
						type="button"
						variant="primary"
						size="md"
						label={copy.confirm}
						onClick={() => {
							copy.onConfirm()
							onClose()
						}}
					/>
				</div>
			</div>
		</Modal>
	)
}
